import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import React, { useMemo, useState } from 'react'

import { downloadVoiceoverCsv } from '../lib/exportCsv'
import { useDoneState } from '../lib/useDoneState'
import { useNotes } from '../lib/useNotes'
import { useTextEdits } from '../lib/useTextEdits'
import { Voiceover } from '../types'

import { VoiceoverChapters } from './VoiceoverChapters'
import { VoiceoverDetail } from './VoiceoverDetail'
import { VoiceoverTable } from './VoiceoverTable'

type View = 'table' | 'detail' | 'chapter'

type Props = {
  rows: Voiceover[]
  modeTitle: string
  csvFileName: string
  // Namespace for the per-browser "done" state (use the mode id so progress is stable).
  storageKey: string
  // Logged-in view injects the "Teilen" button here; the public view passes nothing.
  headerActions?: React.ReactNode
}

const matches = (row: Voiceover, q: string): boolean => {
  const hay =
    `${row.poiTitle} ${row.sectionTitle} ${row.nuggetTitle} ${row.suggestedFileName} ${row.text}`.toLowerCase()
  return hay.includes(q)
}

export const VoiceoverPanel: React.FC<Props> = ({
  rows,
  modeTitle,
  csvFileName,
  storageKey,
  headerActions
}) => {
  const [view, setView] = useState<View>('chapter')
  const [query, setQuery] = useState('')
  const [detailIndex, setDetailIndex] = useState(0)
  const [fontSize, setFontSize] = useState(20)
  const done = useDoneState(storageKey)
  const notes = useNotes(storageKey)
  const textEdits = useTextEdits(storageKey)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? rows.filter((r) => matches(r, q)) : rows
  }, [rows, query])

  const doneInView = useMemo(
    () => filtered.filter((r) => done.isDone(r.suggestedFileName)).length,
    [filtered, done]
  )

  const openDetail = (index: number) => {
    setDetailIndex(index)
    setView('detail')
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          flex: 0
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="chapter">Kapitelansicht</ToggleButton>
          <ToggleButton value="detail">Einzelansicht</ToggleButton>
          <ToggleButton value="table">Übersicht</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Suchen…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 200 }}
        />

        <Typography variant="body2" color="text.secondary">
          {doneInView} / {filtered.length} erledigt
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          disabled={filtered.length === 0}
          onClick={() => downloadVoiceoverCsv(filtered, csvFileName)}
        >
          CSV herunterladen
        </Button>
        {headerActions}
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {filtered.length === 0
          ? (
            <Typography color="text.secondary">
              Keine Voiceovers gefunden.
            </Typography>
            )
          : view === 'table'
            ? (
              <VoiceoverTable
                rows={filtered}
                done={done}
                notes={notes}
                onRowClick={openDetail}
              />
              )
            : view === 'detail'
              ? (
                <VoiceoverDetail
                  rows={filtered}
                  index={Math.min(detailIndex, filtered.length - 1)}
                  modeTitle={modeTitle}
                  fontSize={fontSize}
                  onFontSize={setFontSize}
                  done={done}
                  notes={notes}
                  textEdits={textEdits}
                  onIndexChange={setDetailIndex}
                />
                )
              : (
                <VoiceoverChapters
                  rows={filtered}
                  fontSize={fontSize}
                  onFontSize={setFontSize}
                  done={done}
                  notes={notes}
                  textEdits={textEdits}
                />
                )}
      </Box>
    </Box>
  )
}
